import axios from "axios";

export type UserRole = "admin" | "user";

export interface UserAddress {
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

export interface UserPreferences {
  newsletter?: boolean;
  notifications?: boolean;
  language?: string;
  theme?: string;
}

export interface User {
  _id: string;
  name: string;
  email: string;
  type: UserRole;
  phone?: string;
  avatar?: string;
  bio?: string;
  dateOfBirth?: string;
  address?: UserAddress;
  preferences?: UserPreferences;
  isActive?: boolean;
  step1Completed?: boolean;
  lastLoginAt?: string;
  createdAt?: string;
  updatedAt?: string;
  token?: string;
}

export const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

// ─── Token helpers ────────────────────────────────────────────────────────────

export const getAuthToken = (): string | null => localStorage.getItem("token");
export const setAuthToken = (token: string): void => localStorage.setItem("token", token);
export const getRefreshToken = (): string | null => localStorage.getItem("refreshToken");
export const setRefreshToken = (token: string): void => localStorage.setItem("refreshToken", token);

export const clearAuthToken = (): void => {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");
};

export const isAuthenticated = (): boolean => !!getAuthToken();

// ─── Token-refresh lock (prevents parallel refresh races) ────────────────────
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

function onRefreshDone(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

// ─── Request interceptor: attach access token ─────────────────────────────────
apiClient.interceptors.request.use(
  (config: any) => {
    try {
      const token = getAuthToken();
      if (token) {
        if (!config.headers) config.headers = {} as any;
        config.headers.Authorization = `Bearer ${token}`;
      }

      if (typeof FormData !== "undefined" && config.data instanceof FormData) {
        if (config.headers) {
          delete config.headers["Content-Type"];
          delete config.headers["content-type"];
        }
      }
    } catch (e) {
      console.error("Error in request interceptor:", e);
    }
    return config;
  },
  (error: any) => Promise.reject(error)
);

// ─── Response interceptor: handle 401 with refresh-token retry ───────────────
apiClient.interceptors.response.use(
  (response) => response,
  async (error: any) => {
    const originalRequest = error.config;

    // Never retry login/register/refresh endpoints
    const isAuthEndpoint =
      originalRequest?.url?.includes("/users/login") ||
      originalRequest?.url?.includes("/users/register") ||
      originalRequest?.url?.includes("/auth/refresh") ||
      originalRequest?.url?.includes("/auth/logout");

    if (error.response?.status === 401 && !isAuthEndpoint && !originalRequest._retried) {
      originalRequest._retried = true;

      const storedRefreshToken = getRefreshToken();
      if (!storedRefreshToken) {
        clearAuthToken();
        window.dispatchEvent(new CustomEvent("auth:sessionExpired"));
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue the request until the refresh completes
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(apiClient(originalRequest));
          });
        });
      }

      isRefreshing = true;
      try {
        const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken: storedRefreshToken,
        });

        const { token: newAccessToken, refreshToken: newRefreshToken } = data;
        setAuthToken(newAccessToken);
        if (newRefreshToken) setRefreshToken(newRefreshToken);

        onRefreshDone(newAccessToken);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch {
        // Refresh failed — full logout
        clearAuthToken();
        window.dispatchEvent(new CustomEvent("auth:sessionExpired"));
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ─── Auth service ─────────────────────────────────────────────────────────────

export const authService = {
  async register(userData: {
    name: string;
    email: string;
    password: string;
    type?: UserRole;
    phone?: string;
    referredBy?: string;
  }): Promise<User> {
    try {
      const response = await apiClient.post<{ user: User; token: string; refreshToken?: string }>(
        "/users/register",
        userData
      );

      const { user, token, refreshToken } = response.data;
      if (token) setAuthToken(token);
      if (refreshToken) setRefreshToken(refreshToken);

      return { ...user, token };
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Registration failed";
      throw new Error(errorMessage);
    }
  },

  async login(credentials: { email: string; password: string }): Promise<User> {
    try {
      const response = await apiClient.post<{ user: User; token: string; refreshToken?: string }>(
        "/users/login",
        credentials
      );

      const { user, token, refreshToken } = response.data;
      if (token) setAuthToken(token);
      if (refreshToken) setRefreshToken(refreshToken);

      return { ...user, token };
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Login failed";
      throw new Error(errorMessage);
    }
  },

  async logout(): Promise<void> {
    try {
      // Tell the server to revoke all refresh tokens for this user
      await apiClient.post("/auth/logout");
    } catch {
      // Best-effort — clear locally regardless
    } finally {
      clearAuthToken();
    }
  },

  async updateProfile(
    userId: string | undefined,
    userData: {
      name?: string;
      email?: string;
      phone?: string;
      type?: UserRole;
      bio?: string;
      dateOfBirth?: string;
      address?: UserAddress;
      preferences?: UserPreferences;
    },
    token?: string
  ): Promise<User> {
    try {
      const endpoint = userId ? `/users/${userId}` : "/users/profile";
      const config: any = {};
      if (token) {
        config.headers = { Authorization: `Bearer ${token}` };
      }
      const response = await apiClient.put<User>(endpoint, userData, config);

      if (response.data.token) {
        setAuthToken(response.data.token);
      }

      return response.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Profile update failed";
      throw new Error(errorMessage);
    }
  },

  /**
   * Asks the backend to email a reset link. The response is the same whether
   * or not the address has an account, so never branch on it.
   */
  async requestPasswordReset(email: string): Promise<string> {
    try {
      const response = await apiClient.post<{ message: string }>(
        "/auth/forgot-password",
        { email }
      );
      return response.data.message;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Could not send the reset email. Please try again."
      );
    }
  },

  async resetPassword(token: string, password: string): Promise<string> {
    try {
      const response = await apiClient.post<{ message: string }>(
        "/auth/reset-password",
        { token, password }
      );
      return response.data.message;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Could not reset your password. Please try again."
      );
    }
  },

  async getProfile(): Promise<User & { step1Completed?: boolean }> {
    const response = await apiClient.get<User & { step1Completed?: boolean }>("/users/profile");
    return response.data;
  },

  async completeStep1(): Promise<void> {
    await apiClient.patch("/users/me/step1-complete");
  },

  async uploadPhoto(file: File): Promise<string> {
    const formData = new FormData();
    formData.append("avatar", file);
    const response = await apiClient.post<{ success: boolean; avatarUrl: string }>(
      "/users/profile/photo",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data.avatarUrl;
  },
};
